import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";

export default function Products() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const active = searchParams.get("category") || "All";
    const [loading, setLoading] = useState(true);

    const setActive = (name) => {
        setSearchParams(name === "All" ? {} : { category: name }, { replace: true });
    };

    useEffect(() => {
        Promise.all([api.get("/products"), api.get("/categories")])
            .then(([p, c]) => {
                setProducts(p.data);
                setCategories(c.data);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(
        () => (active === "All" ? products : products.filter((p) => p.category === active)),
        [products, active]
    );

    const activeCategory = categories.find((c) => c.name === active);

    return (
        <main data-testid="products-page" className="bg-slate-50 min-h-screen">
            {/* Page header */}
            <section className="bg-[#0033A0] py-14 md:py-20" data-testid="products-header">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white" data-testid="products-heading">
                        Our Products
                    </h1>
                    <p className="mt-3 text-blue-100 max-w-2xl" data-testid="products-subtitle">
                        A taste of what you'll find in store — fresh meat and poultry from our butcher counter, plus
                        African and Caribbean groceries, spices, fruit, vegetables and international foods.
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                {/* Filter chips */}
                <div className="flex flex-wrap gap-2 mb-10" data-testid="category-filters" role="tablist" aria-label="Filter products by category">
                    {["All", ...categories.map((c) => c.name)].map((name) => (
                        <button
                            key={name}
                            type="button"
                            role="tab"
                            aria-selected={active === name}
                            data-testid={`filter-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            onClick={() => setActive(name)}
                            className={`rounded-full px-5 py-2.5 text-sm font-heading font-bold transition-colors ${
                                active === name
                                    ? "bg-[#0033A0] text-white"
                                    : "bg-white text-[#0033A0] border-2 border-[#0033A0] hover:bg-slate-100"
                            }`}
                        >
                            {name}
                        </button>
                    ))}
                </div>

                {/* Category intro card for non-meat categories */}
                {activeCategory && filtered.length === 0 && (
                    <div className="mb-10 rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm md:flex" data-testid="category-intro">
                        <div className="md:w-1/3 h-56 md:h-auto">
                            <img src={activeCategory.image} alt={activeCategory.name} className="h-full w-full object-cover" />
                        </div>
                        <div className="p-6 md:p-10 flex flex-col justify-center">
                            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900" data-testid="category-intro-name">
                                {activeCategory.name}
                            </h2>
                            <p className="mt-3 text-slate-600 leading-relaxed" data-testid="category-intro-description">
                                {activeCategory.description}
                            </p>
                            <p className="mt-4 text-sm font-bold text-[#0033A0]">
                                Available in store — pop in or call us to check what's on the shelves today.
                            </p>
                        </div>
                    </div>
                )}

                {/* Product grid */}
                {loading ? (
                    <p className="text-slate-500" data-testid="products-loading">Loading products…</p>
                ) : filtered.length > 0 ? (
                    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-8" data-testid="products-grid">
                        {filtered.map((p) => (
                            <ProductCard key={p.id} product={p} testId={`product-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                        ))}
                    </div>
                ) : (
                    !activeCategory && <p className="text-slate-500" data-testid="products-empty">No products to show yet — check back soon.</p>
                )}

                <p className="mt-12 text-center text-sm text-slate-500 max-w-xl mx-auto" data-testid="products-note">
                    Pop in to see our full range — the shelves are always changing. Call us on{" "}
                    <a href="tel:+441182295059" className="font-bold text-[#0033A0] hover:underline" data-testid="products-call-link">
                        0118 229 5059
                    </a>{" "}
                    if you're looking for something specific.
                </p>
            </div>
        </main>
    );
}
